import React, { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { useBiz, errText } from "./i18n";
import { Sheet, Field, Row, inp, btnMain, btnSoft, btnIcon } from "./ui";

const PRESETS = [["alcohol", "ml"], ["oil", "grams"], ["bottle", "pieces"], ["pump", "pieces"], ["cap", "pieces"], ["label", "pieces"], ["shrink", "pieces"], ["box", "pieces"], ["extra", ""]];
let seq = 0;
const row = (x) => ({ key: `r${++seq}`, id: x.id, name: x.name ?? "", qty: x.qty ?? "", unit: x.unit ?? "", cost: x.cost ?? "" });
const filled = (r) => String(r.name).trim() && r.cost !== "";

const ExpenseRows = ({ rows, setRows, t, onRemove }) => {
  const upd = (k, f, v) => setRows(rows.map((r) => (r.key === k ? { ...r, [f]: v } : r)));
  return (
    <div className="space-y-2" data-testid="batch-expense-rows">
      {rows.map((r, i) => (
        <div key={r.key} className="grid grid-cols-12 gap-2 items-center bg-white border border-[#EEE5D8] rounded-xl p-2">
          <input value={r.name} onChange={(e) => upd(r.key, "name", e.target.value)} placeholder={t("item")} data-testid={`batch-exp-name-${i}`} className={`${inp} col-span-12 sm:col-span-5 !py-2.5`} />
          <input type="number" step="any" min="0" value={r.qty} onChange={(e) => upd(r.key, "qty", e.target.value)} placeholder={t("qty")} data-testid={`batch-exp-qty-${i}`} className={`${inp} col-span-4 sm:col-span-2 !py-2.5`} />
          <input value={r.unit} onChange={(e) => upd(r.key, "unit", e.target.value)} placeholder={t("unit")} data-testid={`batch-exp-unit-${i}`} className={`${inp} col-span-3 sm:col-span-2 !py-2.5`} />
          <input type="number" step="any" min="0" value={r.cost} onChange={(e) => upd(r.key, "cost", e.target.value)} placeholder="AED" data-testid={`batch-exp-cost-${i}`} className={`${inp} col-span-4 sm:col-span-2 !py-2.5`} />
          <button type="button" onClick={() => onRemove(r)} data-testid={`batch-exp-del-${i}`} className={`${btnIcon} col-span-1`}><Trash2 size={16} /></button>
        </div>
      ))}
      <button type="button" onClick={() => setRows([...rows, row({})])} data-testid="batch-exp-add" className={btnSoft}><Plus size={15} />{t("add_expense")}</button>
    </div>
  );
};

const Totals = ({ rows, bottles, price, t, aed }) => {
  const total = rows.filter(filled).reduce((s, r) => s + Number(r.cost || 0), 0);
  const cpb = Number(bottles) > 0 ? total / Number(bottles) : null;
  return (
    <div className="bg-white border border-[#EEE5D8] rounded-2xl px-4 py-2 mt-4" data-testid="batch-live-totals">
      <Row label={t("total_cost")} value={aed(total)} strong testid="batch-live-total" />
      <Row label={t("cost_per_bottle")} value={aed(cpb)} strong testid="batch-live-cpb" />
      <Row label={t("profit_per_bottle")} value={cpb === null || price === "" ? "—" : aed(Number(price) - cpb)} />
    </div>
  );
};

const saveLines = async (batchId, rows, original) => {
  const keep = rows.filter(filled);
  for (const r of keep) {
    const body = { name: r.name, qty: r.qty === "" ? null : r.qty, unit: r.unit, cost: r.cost, batch_id: batchId };
    const o = original.find((x) => x.id === r.id);
    if (!r.id) await api.post("/admin/simple/expenses", body);
    else if (o && (o.name !== r.name || String(o.qty ?? "") !== String(r.qty) || o.unit !== r.unit || String(o.cost) !== String(r.cost))) await api.put(`/admin/simple/expenses/${r.id}`, body);
  }
};

const BatchFields = ({ f, set, products, locked, t }) => (
  <div className="grid grid-cols-2 gap-3">
    <Field label={t("perfume")} className="col-span-2 sm:col-span-1">
      <select disabled={locked} value={f.product_id} onChange={set("product_id")} data-testid="batch-perfume" className={inp}>
        {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
    </Field>
    <Field label={t("batch_number")} className="col-span-2 sm:col-span-1"><input value={f.batch_number} onChange={set("batch_number")} placeholder={t("auto")} data-testid="batch-number" className={inp} /></Field>
    <Field label={t("bottles")}><input required type="number" min="1" value={f.bottles} onChange={set("bottles")} data-testid="batch-bottles" className={inp} /></Field>
    <Field label={t("sell_price")}><input required type="number" step="any" min="0" value={f.sell_price} onChange={set("sell_price")} data-testid="batch-sell-price" className={inp} /></Field>
  </div>
);

export const BatchEditor = ({ batch, products, onClose, onSaved }) => {
  const { t, aed } = useBiz();
  const [f, setF] = useState(batch ? { product_id: batch.product_id, batch_number: batch.batch_number, bottles: batch.bottles, sell_price: batch.sell_price }
    : { product_id: products[0]?.id || "", batch_number: "", bottles: "", sell_price: "" });
  const [rows, setRows] = useState(batch ? batch.expenses.map(row) : PRESETS.map(([k, u]) => row({ name: t(k), unit: u })));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const remove = async (r) => {
    if (r.id) {
      if (!window.confirm(t("confirm_delete_exp"))) return;
      try {
        await api.delete(`/admin/simple/expenses/${r.id}`);
      } catch (e) {
        return toast.error(errText(e));
      }
    }
    setRows(rows.filter((x) => x.key !== r.key));
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (batch) {
        await api.put(`/admin/simple/batches/${batch.id}`, { batch_number: f.batch_number, bottles: f.bottles, sell_price: f.sell_price });
        await saveLines(batch.id, rows, batch.expenses);
      } else {
        const lines = rows.filter(filled).map((r) => ({ name: r.name, qty: r.qty === "" ? null : r.qty, unit: r.unit, cost: r.cost }));
        await api.post("/admin/simple/batches", { ...f, expenses: lines });
      }
      toast.success(t("saved"));
      onSaved();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={batch ? `${t("edit")} · ${batch.batch_number}` : t("new_batch")} onClose={onClose} testid="batch-editor">
      <form onSubmit={save}>
        <BatchFields f={f} set={set} products={products} locked={!!batch} t={t} />
        <p className="text-[13px] text-[#6B645A] mt-6 mb-2">{t("batch_expenses")}</p>
        <ExpenseRows rows={rows} setRows={setRows} t={t} onRemove={remove} />
        <Totals rows={rows} bottles={f.bottles} price={f.sell_price} t={t} aed={aed} />
        <button disabled={busy} data-testid="batch-save" className={`${btnMain} w-full mt-5`}>{batch ? t("save_changes") : t("save")}</button>
      </form>
    </Sheet>
  );
};
